import { useApolloClient, useQuery } from "@apollo/client";
import { Alert, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import { MapEditorGetAllLayerFeaturesDocument } from "~/modules/mapEditor/api/useMapEditorGetAllLayerFeatures.generated";
import { MapEditorEcologicalFunctionPanel } from "~/modules/mapEditor/components/MapEditorEcologicalFunctionPanel";
import { MapEditorFeatureOtherPanel } from "~/modules/mapEditor/components/MapEditorFeatureOtherPanel";
import { MapEditorObservationMediaPointPanel } from "~/modules/mapEditor/components/MapEditorObservationMediaPointPanel";
import { MapEditorObservationPanel } from "~/modules/mapEditor/components/MapEditorObservationPanel";
import { MapEditorObservationPanelEcologicalFunctionFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/EcologicalFunctionCard/fragments.generated";
import { MapEditorObservationPanelObservationMediaPointFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/components/ObservationMediaPointCard/fragments.generated";
import { MapEditorObservationPanelEcologicalFunctionCreatedFragment } from "~/modules/mapEditor/components/MapEditorObservationPanel/fragments.generated";
import { MapEditorPlannerAreaPanel } from "~/modules/mapEditor/components/MapEditorPlannerAreaPanel";
import { MapEditorFeatureListRoute_QueryDocument } from "~/modules/mapEditor/routes/MapEditorFeatureListRoute/queries.generated";
import { useWithSearch } from "~/modules/project/hooks/useWithSearch";
import { splitGqlUnion } from "~/shared/helpers/splitGqlUnion";

import { ProjectMapEditorLayerFeatureScreenDocument } from "./queries.generated";

export function ProjectMapEditorLayerFeatureScreen() {
    const { t } = useTranslation("mapEditor");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const { mapEditorFeatureId } = useParams();
    if (!mapEditorFeatureId) throw new Error("Missing mapEditorFeatureId");

    const client = useApolloClient();
    const navigate = useNavigate();
    const withSearch = useWithSearch();

    const backHref = withSearch("..");

    const { data, loading, error } = useQuery(ProjectMapEditorLayerFeatureScreenDocument, {
        variables: { id: mapEditorFeatureId },
        fetchPolicy: "cache-first",
    });

    const feature = data?.mapEditorFeatureGetById?.data;

    const handleFeatureDeleted = async () => {
        await client.refetchQueries({
            include: [MapEditorFeatureListRoute_QueryDocument, MapEditorGetAllLayerFeaturesDocument],
        });
    };

    // Opens the newly created function in place of the observation panel, and shows it on the map and in the feature list.
    const handleEcologicalFunctionCreated = async (
        ecologicalFunction: MapEditorObservationPanelEcologicalFunctionCreatedFragment,
    ) => {
        navigate(withSearch(createHrefToCreatedEcologicalFunction(ecologicalFunction)), { relative: "path" });

        await client.refetchQueries({
            include: [MapEditorFeatureListRoute_QueryDocument, MapEditorGetAllLayerFeaturesDocument],
        });
    };

    if (error) {
        return (
            <Alert severity="error">
                <Typography>{error.message}</Typography>
            </Alert>
        );
    }

    if (loading || !feature) {
        return null;
    }

    return splitGqlUnion(feature, {
        GqlEcologicalFunction: () => (
            <MapEditorEcologicalFunctionPanel
                backHref={backHref}
                createHrefToObservation={element => withSearch(createHrefToObservation(element))}
                mapEditorFeatureId={mapEditorFeatureId}
                onEcologicalFunctionDeleted={handleFeatureDeleted}
                readonly={!isAdmin}
                title={t("featureDetailEcologicalFunction.title")}
            />
        ),
        GqlObservation: () => (
            <MapEditorObservationPanel
                backHref={backHref}
                createHrefToEcologicalFunction={element => withSearch(createHrefToEcologicalFunction(element))}
                createHrefToObservationMediaPointCard={element => withSearch(createHrefToObservationMediaPointCard(element))}
                mapEditorFeatureId={mapEditorFeatureId}
                onEcologicalFunctionCreated={handleEcologicalFunctionCreated}
                onObservationDeleted={handleFeatureDeleted}
                readonly={!isAdmin}
                title={t("featureDetailObservation.title")}
            />
        ),
        GqlPlannerArea: () => (
            <MapEditorPlannerAreaPanel
                backHref={backHref}
                mapEditorFeatureId={mapEditorFeatureId}
                readonly={!isAdmin}
                title={t("featureDetailArea.title")}
            />
        ),
        GqlMapEditorFeatureDataTypeOther: () => (
            <MapEditorFeatureOtherPanel
                backHref={backHref}
                mapEditorFeatureId={mapEditorFeatureId}
                readonly={!isAdmin}
                title={t("featureDetailOther.title")}
            />
        ),
        GqlMediaPoint: () => (
            <MapEditorObservationMediaPointPanel
                backHref={backHref}
                createHrefToObservation={element => withSearch(createHrefToObservation(element))}
                mapEditorFeatureId={mapEditorFeatureId}
                title={t("featureDetailMediaPoint.title")}
            />
        ),
    });
}

function createHrefToEcologicalFunction(element: MapEditorObservationPanelEcologicalFunctionFragment) {
    return `../../${element.feature.layerId}/${element.featureId}`;
}

function createHrefToCreatedEcologicalFunction(element: MapEditorObservationPanelEcologicalFunctionCreatedFragment) {
    return `../../${element.feature.layerId}/${element.feature.id}`;
}

function createHrefToObservationMediaPointCard(element: MapEditorObservationPanelObservationMediaPointFragment) {
    return `../../${element.feature.layerId}/${element.mapEditorFeatureId}`;
}

function createHrefToObservation(element: { featureId: string; feature: { layerId: string } }) {
    return `../../${element.feature.layerId}/${element.featureId}`;
}
